import Head from 'next/head';
import Header from '../components/Header';
import Hero from '../components/Hero';
import Footer from '../components/Footer';
import { generateWebPageSchema } from '../utils/generateWebPageSchema';

export default function TermsOfService() {
  const base = 'https://modernbuildremodel.com';
  const pageUrl = `${base}/terms-of-service`;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": base },
      { "@type": "ListItem", "position": 2, "name": "Terms of Service", "item": pageUrl }
    ]
  };
  
  return (
    <>
      <Head>
        <title>Terms of Service | Modern Build</title>
        <meta name="title" content="Terms of Service | Modern Build" />
        <meta
          name="description"
          content="Review the Terms of Service for Modern Build Home Remodeling. Learn how you may use our website and the rights and responsibilities related to our services."
        />
        <meta name="author" content="Modern Build Home Remodeling" />
        <meta name="keywords" content="terms of service, legal, website use, user agreement, remodeling website policy, Modern Build" />
        <link rel="canonical" href="https://modernbuildremodel.com/terms-of-service" />

        {/* Open Graph */}
        <meta property="og:title" content="Terms of Service | Modern Build" />
        <meta property="og:description" content="Understand the terms governing your use of Modern Build's website and services." />
        <meta property="og:image" content="https://modernbuildremodel.com/hero/best-home-remodling-contractor-in-los-angeles.webp" />
        <meta property="og:url" content="https://modernbuildremodel.com/terms-of-service" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Terms of Service | Modern Build" />
        <meta name="twitter:description" content="Read the terms that apply when you use our remodeling services and website." />
        <meta name="twitter:image" content="https://modernbuildremodel.com/hero/best-home-remodling-contractor-in-los-angeles.webp" />
        <meta name="twitter:site" content="@modernbuildhome" />

        {/* JSON-LD: WebPage */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title: 'Terms of Service | Modern Build',
                description: 'Read the terms of service for Modern Build Home Remodeling, including website use and your rights and responsibilities.',
                slug: '/terms-of-service'
              })
            )
          }}
        />

        {/* JSON-LD: Breadcrumb */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      </Head>

      <Header />
      <Hero category="TermsOfService" page="page" hasContentH1={true}/>
<div className="container mx-auto px-4 py-10 max-w-4xl">
  <div className="prose prose-lg max-w-none text-md text-black">
    <h1 className="text-3xl font-gilda mb-6">Terms of Service</h1>

    <p>
      Welcome to Modern Build Home Remodeling. These Terms of Service govern your use of our website at <a href="https://modernbuildremodel.com" className="underline">modernbuildremodel.com</a> and any referral or coordination services we provide. By accessing or using our site, you agree to these terms.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">1. Our Role</h2>
    <p>
      Modern Build Home Remodeling is a marketing and referral platform. We connect homeowners with independent contractors who represent that they are licensed, bonded, and insured. We do not perform construction, remodeling, repair, or installation work. We are not a general contractor, not a construction manager, and not a home improvement salesperson for any specific project.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">2. Independent Contractors</h2>
    <p>
      All physical work is performed by independent businesses that are not employees, agents, partners, or joint venturers of Modern Build. We do not supervise, direct, or control contractor work. Referrals are provided for convenience only, not as an endorsement or warranty. You are responsible for selecting your contractor and verifying license, insurance, references, and scope in writing.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">3. Estimates, Matching, and Referral Fees</h2>
    <p>
      If you request a referral, you authorize us to share your contact details and project information with one or more contractors so they can contact you directly. Estimates are provided by contractors and may change based on site conditions, permits, and selections. We may receive a marketing or referral fee from a contractor if you hire them, this does not increase your price and does not determine which contractors are shown to you.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">4. Permits and Compliance</h2>
    <p>
      Contractors are responsible for obtaining required permits, inspections, and approvals, and for complying with all laws and building codes. You are responsible for timely property access and for confirming scope, finishes, and selections with your contractor in writing.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">5. Payments</h2>
    <p>
      Payments for construction or remodeling services are made directly to the contractor, unless you and the contractor agree otherwise in writing. Modern Build does not collect or hold progress payments for contractor services and is not a party to your construction contract.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">6. Disclaimers</h2>
    <p>
      Your use of the site and any services is at your sole risk. The site and information are provided on an as is and as available basis. To the maximum extent permitted by law, we disclaim all warranties, whether express or implied, including implied warranties of merchantability, fitness for a particular purpose, and non infringement. We do not warrant that the site will be uninterrupted or error free, or that any contractor will meet your requirements.
    </p>
    <p>
      All images and videos displayed on this website are generated using artificial intelligence and are not photographs or recordings of actual projects. They are for illustrative purposes only and should not be interpreted as representations of completed work by Modern Build or its contractors.
    </p>
    <h2 className="text-xl font-semibold mt-6 mb-2">7. Limitation of Liability</h2>
    <p>
      To the maximum extent permitted by law, Modern Build Home Remodeling, and its officers, directors, employees, and affiliates, will not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, loss of data, property damage, personal injury, delays, or claims arising out of or related to contractor work. Our total liability for any claims arising from or relating to the site or services will not exceed the greater of 100 dollars or the amount you paid to us, if any, in the twelve months before the claim. Nothing in these terms limits liability where such limitation is not allowed by law.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">8. Indemnification</h2>
    <p>
      You agree to defend, indemnify, and hold harmless Modern Build Home Remodeling from and against any claims, damages, losses, liabilities, costs, and expenses, including reasonable attorneys fees, arising out of or related to your use of the site, your communications with contractors, your project decisions, or your breach of these terms, except to the extent caused by our willful misconduct.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">9. User Responsibilities</h2>
    <ul className="list-disc pl-6 space-y-2 mb-4">
      <li>Provide accurate and complete information in your requests</li>
      <li>Verify contractor license and insurance at <a className="underline" href="https://www.cslb.ca.gov/">cslb.ca.gov</a></li>
      <li>Review references, proposals, change orders, and warranties</li>
      <li>Enter into a written contract that defines scope, price, timeline, and warranties</li>
      <li>Do not use the site for unlawful or harmful activity</li>
    </ul>

    <h2 className="text-xl font-semibold mt-6 mb-2">10. Communications Consent</h2>
    <p>
      By submitting your information, you consent to be contacted by us and by referred contractors at the phone number or email you provide. This may include calls and text messages about your project. Message and data rates may apply. You can opt out of non transactional messages at any time.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">11. Intellectual Property</h2>
    <p>
      All content on the website, including text, graphics, logos, and images, is the property of Modern Build or its licensors and is protected under applicable copyright and trademark laws. You may not copy, modify, distribute, or create derivative works without prior written consent.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">12. Prohibited Use</h2>
    <ul className="list-disc pl-6 space-y-2 mb-4">
      <li>No scraping, crawling, or automated data collection</li>
      <li>No posting of harmful, misleading, or infringing content</li>
      <li>No attempting to gain unauthorized access to the site or our systems</li>
    </ul>

    <h2 className="text-xl font-semibold mt-6 mb-2">13. Third Party Links and Services</h2>
    <p>
      The site may contain links to third party websites or services. We are not responsible for the content, privacy practices, or availability of those third parties. Your use of third party sites is at your own risk.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">14. Podcast and Media Content Disclaimer</h2>
    <p>
      Modern Build Home Remodeling produces and hosts audio and video content, including podcasts, interviews, and related media featuring contractors, home service professionals, and industry participants.
    </p>
    <br />
    <p>
      All content provided through our podcasts, videos, website, social media, or other platforms is for general informational and entertainment purposes only. It reflects personal opinions, experiences, and perspectives of hosts and guests and does not constitute legal, financial, construction, engineering, safety, insurance, or professional advice.
    </p>
    <br />
    <p>
      We do not guarantee the accuracy, completeness, or reliability of any statements made by hosts or guests. Guests speak for themselves and do not represent the views of Modern Build. Participation in our media does not constitute an endorsement of any individual, company, product, or service.
    </p>
    <br />
    <p>
      You are solely responsible for verifying any information obtained through our content and for conducting your own independent research, due diligence, and consultations with qualified professionals before making any decisions related to construction, remodeling, budgeting, hiring contractors, permits, insurance, or safety.
    </p>
    <br />
    <p>
      Modern Build Home Remodeling is not responsible for any actions, losses, damages, disputes, injuries, delays, or expenses that may result from reliance on podcast or media content.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">15. Termination</h2>
    <p>
      We may suspend or terminate your access to the site at any time if we believe you have violated these terms or for any reason in our sole discretion. Sections that by their nature should survive termination will survive.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">16. Dispute Resolution and Arbitration</h2>
    <p>
      Please read this section carefully. It requires binding arbitration on an individual basis for most disputes. You and Modern Build agree to resolve any dispute, claim, or controversy arising out of or relating to these terms or the site through final and binding arbitration administered by JAMS in Los Angeles County, California, under the JAMS Streamlined Rules. Either party may seek relief in small claims court for qualifying claims. You agree to bring claims only in your individual capacity, not as a plaintiff or class member in any class or representative action. If this class action waiver is found unenforceable, the arbitration provision will be void as to that claim.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">17. Governing Law and Venue</h2>
    <p>
      These terms are governed by the laws of the State of California, without regard to conflict of laws rules. Subject to the arbitration provision, the exclusive venue for any permitted court action will be the state and federal courts located in Los Angeles County, California.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">18. Changes to These Terms</h2>
    <p>
      We may update these terms at any time. The updated terms will be posted on this page with a new effective date. Your continued use of the site after changes means you accept the updated terms.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">19. Entire Agreement and Severability</h2>
    <p>
      These terms, together with our Privacy Policy, make up the entire agreement between you and Modern Build regarding the site. If any provision is found unenforceable, the remaining provisions will remain in full force and effect.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">20. Contact Us</h2>
    <p>Questions about these terms, contact us at:</p>
    <ul className="list-disc pl-6 space-y-2 mb-4">
      <li>Phone, (323) 300 4130</li>
      <li>Email, <a href="mailto:remodel@modernbuildremodel.com" className="underline">remodel@modernbuildremodel.com</a></li>
      <li>Address, 4222 Longridge Ave, Studio City, CA 91604, USA</li>
    </ul>

    <p className="text-sm text-gray-600 mt-6">Last updated, September 2025</p>
  </div>
</div>




      <Footer />
    </>
  );
}
